import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';

import { UseOutletContextBankStatement } from './BankStatement';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { BankStatementDA } from '../../../../services/admin/bank.statement.service';
import { BankStatement, AutoMatchJob } from '../../../../models/bank.statement.model';
import { TreasuryAccount } from '../../../../models/treasury.account.model';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { modalShow } from '../../../../utils/modal.util';

import AppPreloader from '../../../../components/Preloader';
import AppModalBankStatementImport from '../../../../components/modal/ModalBankStatementImport';
import { GlobalSwal } from '../../../../styles/swal.style';

export interface BankStatementListPageProps {};

const POLLING_INTERVAL_MS = 5000

const BankStatementListPage: React.FunctionComponent<BankStatementListPageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany} = UseOutletContextBankStatement()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [mounted, setMounted] = useState(false)
  const [statements, setStatements] = useState<BankStatement[] | null>(null)
  const [selectedTreasuryId, setSelectedTreasuryId] = useState<string>(searchParams.get('id_treasury_account') ?? '')
  const [pagination, setPagination] = useState({page: 1, limit: 20, total: 0, pages: 0})
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isPollingRef = useRef(false)

  const loadStatements = useCallback(async (showSpinner: boolean = false) => {
    if (!companyForUser) return

    if (showSpinner) setStatements(null)

    try {
      const response = await BankStatementDA.getStatements(companyForUser.id, {
        ...(selectedTreasuryId && { id_treasury_account: selectedTreasuryId }),
        page: pagination.page,
        limit: pagination.limit,
        sort_order: sortOrder,
      })

      if (response.status === 200 || response.status === 304) {
        const payload = response.data ?? {}
        const items: BankStatement[] = Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : [])
        setStatements(items)
        if (payload.pagination) {
          setPagination(prev => ({...prev, total: payload.pagination.total ?? 0, pages: payload.pagination.pages ?? 1}))
        }
      } else {
        setStatements([])
      }
    } catch (err) {
      console.error('[BankStatementList] load failed', err)
      setStatements([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, selectedTreasuryId, pagination.page, pagination.limit, sortOrder])

  const hasActiveJobs = useCallback((stmts: BankStatement[]): boolean => {
    return stmts.some(s =>
      s.auto_match_job?.status === 'queued' ||
      s.auto_match_job?.status === 'running'
    )
  }, [])

  const updatePollingState = useCallback((stmts: BankStatement[]) => {
    const shouldPoll = hasActiveJobs(stmts)

    if (shouldPoll && !isPollingRef.current) {
      isPollingRef.current = true
      pollingIntervalRef.current = setInterval(() => {
        loadStatements(false)
      }, POLLING_INTERVAL_MS)
    } else if (!shouldPoll && isPollingRef.current) {
      isPollingRef.current = false
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [hasActiveJobs, loadStatements])

  useEffect(() => {
    loadStatements(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, selectedTreasuryId, pagination.page, sortOrder])

  useEffect(() => {
    if (statements) {
      updatePollingState(statements)
    }
  }, [statements, updatePollingState])

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        isPollingRef.current = false
      }
    }
  }, [])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.treasuryAccounts, branch: lang.labels.bankStatements}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [deletingId, setDeletingId] = useState<string | null>(null)

  const getMatchedCount = (stmt: BankStatement): number => {
    return stmt.matched_lines_count ?? 0
  }

  const getTotalLines = (stmt: BankStatement): number => {
    return stmt.total_lines_count ?? 0
  }

  const handleDeleteStatement = async (stmt: BankStatement) => {
    if (!companyForUser || !stmt.id) return
    const matchedCount = getMatchedCount(stmt)
    const totalLines = getTotalLines(stmt)
    const unreconciled = totalLines - matchedCount
    const treasuryAccount = treasuryAccountsForCompany?.find((t: TreasuryAccount) => t.id === stmt.id_treasury_account)

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmDeleteStatementTitle ?? 'Delete this bank statement?',
      html: `<div class="text-start">
        <p class="mb-2"><strong>${treasuryAccount?.name ?? '—'}</strong>${treasuryAccount?.iban ? ` — ${treasuryAccount.iban}` : ''}</p>
        <p class="mb-2">${lang.labels.period ?? 'Period'}: ${dateFormat(lang.code, stmt.period_start)} – ${dateFormat(lang.code, stmt.period_end)}</p>
        <p class="text-danger">${(lang.labels.confirmDeleteStatementBody ?? 'This action deletes the statement and its {count} unreconciled transaction(s).').replace('{count}', String(unreconciled))}</p>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete ?? 'Yes, delete',
      cancelButtonText: lang.labels.cancel,
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary' },
    })

    if (!result.isConfirmed) return

    setDeletingId(stmt.id)
    try {
      const response = await BankStatementDA.deleteStatement(companyForUser.id, stmt.id)
      if (response.status === 200) {
        GlobalSwal.fire({ text: lang.labels.statementDeleted ?? 'Statement deleted successfully.', icon: 'success', timer: 2000, showConfirmButton: false })
        loadStatements(false)
      } else if (response.status === 409 && response.data?.code === 'STATEMENT_HAS_MATCHED_LINES') {
        const serverCount = response.data.matched_count ?? matchedCount
        GlobalSwal.fire({
          text: (lang.labels.statementHasMatchedLinesError ?? 'This statement has {count} reconciled transaction(s). Unlink them before deleting.').replace('{count}', String(serverCount)),
          icon: 'error',
        })
        loadStatements(false)
      } else {
        GlobalSwal.fire({ text: response.data?.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
      }
    } catch (err) {
      console.error('[BankStatementList] delete failed', err)
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
    } finally {
      setDeletingId(null)
    }
  }

  const [invalidatingId, setInvalidatingId] = useState<string | null>(null)

  const handleInvalidateStatement = async (stmt: BankStatement) => {
    if (!companyForUser || !stmt.id) return
    const totalLines = getTotalLines(stmt)
    const treasuryAccount = treasuryAccountsForCompany?.find((t: TreasuryAccount) => t.id === stmt.id_treasury_account)

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmInvalidateStatementTitle ?? 'Invalidate this bank statement?',
      html: `<div class="text-start">
        <p class="mb-2"><strong>${treasuryAccount?.name ?? '—'}</strong>${treasuryAccount?.iban ? ` — ${treasuryAccount.iban}` : ''}</p>
        <p class="mb-2">${lang.labels.period ?? 'Period'}: ${dateFormat(lang.code, stmt.period_start)} – ${dateFormat(lang.code, stmt.period_end)}</p>
        <p>${(lang.labels.confirmInvalidateStatementBody ?? 'The statement will revert to draft for correction. Its {count} transaction(s) will no longer be available for reconciliation. The imported document remains attached.').replace('{count}', String(totalLines))}</p>
      </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.confirmInvalidate ?? 'Yes, invalidate',
      cancelButtonText: lang.labels.cancel,
      customClass: { confirmButton: 'btn btn-secondary', cancelButton: 'btn btn-light' },
    })

    if (!result.isConfirmed) return

    setInvalidatingId(stmt.id)
    try {
      const response = await BankStatementDA.invalidateStatement(companyForUser.id, stmt.id)
      if (response.status === 200) {
        GlobalSwal.fire({ text: lang.labels.statementInvalidated ?? 'Statement reverted to draft.', icon: 'success', timer: 2000, showConfirmButton: false })
        loadStatements(false)
      } else if (response.status === 409 && response.data?.code === 'STATEMENT_HAS_MATCHED_LINES') {
        const serverCount = response.data.matched_count ?? getMatchedCount(stmt)
        GlobalSwal.fire({
          text: (lang.labels.statementHasMatchedLinesError ?? 'This statement has {count} reconciled transaction(s). Unlink them before deleting.').replace('{count}', String(serverCount)),
          icon: 'error',
        })
        loadStatements(false)
      } else {
        GlobalSwal.fire({ text: response.data?.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
      }
    } catch (err) {
      console.error('[BankStatementList] invalidate failed', err)
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
    } finally {
      setInvalidatingId(null)
    }
  }

  const statementsNeedingAttention = (statements ?? []).filter(stmt => {
    const job = stmt.auto_match_job
    if (job?.status === 'queued' || job?.status === 'running') return false
    if (job?.status === 'failed') return true
    const matched = stmt.matched_lines_count ?? 0
    const total = stmt.total_lines_count ?? 0
    return total > 0 && matched < total
  })

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && treasuryAccountsForCompany
        ?
        <>
          { statementsNeedingAttention.length > 0 &&
            <div className="card border border-warning mb-5">
              <div className="card-body py-4 px-5">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div className="symbol symbol-40px">
                      <span className="symbol-label bg-light-warning">
                        <span className="svg-icon svg-icon-2x text-warning">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="currentColor"/>
                            <path d="M12 7C11.4 7 11 7.4 11 8V13C11 13.6 11.4 14 12 14C12.6 14 13 13.6 13 13V8C13 7.4 12.6 7 12 7Z" fill="currentColor"/>
                            <path d="M12 15C11.4 15 11 15.4 11 16C11 16.6 11.4 17 12 17C12.6 17 13 16.6 13 16C13 15.4 12.6 15 12 15Z" fill="currentColor"/>
                          </svg>
                        </span>
                      </span>
                    </div>
                    <div>
                      <div className="fw-bold fs-6">
                        {statementsNeedingAttention.length === 1
                          ? lang.labels.oneStatementNeedingAttention
                          : `${statementsNeedingAttention.length} ${lang.labels.statementsNeedingAttention}`
                        }
                      </div>
                      <small className="text-muted">{lang.labels.someTransactionsNotReconciled}</small>
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => navigate(`/main/treasury/bank-statement/view/${statementsNeedingAttention[0].id}`)}
                  >
                    {lang.labels.continueReconciliation}
                  </button>
                </div>
              </div>
            </div>
          }
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <Form.Select bsPrefix="form-select w-300px" value={selectedTreasuryId} onChange={(e) => { setSelectedTreasuryId(e.target.value); setPagination(prev => ({...prev, page: 1})) }}>
                  <option value="">{lang.labels.allTreasuryAccounts}</option>
                  { treasuryAccountsForCompany.map((item: TreasuryAccount, index: number) => (
                    <option key={index} value={item.id}>{item.name}{item.iban ? ` — ${item.iban}` : ''}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="card-toolbar d-flex gap-2">
                <button className="btn btn-primary" type="button" onClick={() => modalShow('modal-bank-statement-import')}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1" transform="rotate(90 12.75 4.25)" fill="black" />
                        <path d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z" fill="black" />
                        <path d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z" fill="#C4C4C4" />
                      </svg>
                    </span>
                    {lang.labels.importStatement}
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body p-0">
              <div className="dataTables_wrapper border border-2 rounded">
                <div className="table-responsive rounded">
                  <table className="table align-middle table-row-bordered gx-10 mb-2">
                    <thead className="bg-light border-bottom-2">
                      <tr className="text-dark text-uppercase fw-bolder fs-7">
                        <th className="min-w-100px text-start">{lang.labels.statementDate}</th>
                        <th className="min-w-150px text-start cursor-pointer user-select-none" onClick={() => { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); setPagination(prev => ({...prev, page: 1})) }}>
                          {lang.labels.period} {sortOrder === 'desc' ? '↓' : '↑'}
                        </th>
                        <th className="min-w-100px text-end text-nowrap">{lang.labels.openingBalance}</th>
                        <th className="min-w-100px text-end text-nowrap">{lang.labels.closingBalance}</th>
                        <th className="min-w-150px text-start">{lang.labels.reconciliation}</th>
                        <th className="w-80px text-center">{lang.labels.format}</th>
                        <th className="w-80px text-center">{lang.labels.status}</th>
                        <th className="w-100px"></th>
                      </tr>
                    </thead>
                    { statements
                      ?
                      <>
                        { statements.length > 0
                          ?
                          <tbody className="text-secondary fw-bold">
                            { statements.map((item, index) => {
                              const matched = item.matched_lines_count
                              const total = item.total_lines_count
                              const hasLiveCounts = typeof matched === 'number' && typeof total === 'number'
                              const isFullyReconciled = hasLiveCounts && matched === total && total > 0

                              return (
                                <tr key={index} className="row-dashed">
                                  <td className="text-nowrap">{dateFormat(lang.code, item.statement_date)}</td>
                                  <td className="text-nowrap">{dateFormat(lang.code, item.period_start)} – {dateFormat(lang.code, item.period_end)}</td>
                                  <td className="text-end text-nowrap">{formatMoney(item.opening_balance)}</td>
                                  <td className="text-end text-nowrap">{formatMoney(item.closing_balance)}</td>
                                  <td><ReconciliationProgressCell matched={matched} total={total} job={item.auto_match_job} lang={lang} /></td>
                                  <td className="text-center"><span className="badge badge-light-info">{item.format}</span></td>
                                  <td className="text-center">
                                    { item.import_status === 'draft'
                                      ? <span className="badge badge-light-warning">{lang.labels.draft}</span>
                                      : <span className="badge badge-light-success">{lang.labels.confirmed ?? 'Confirmed'}</span>
                                    }
                                  </td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Link to={`/main/treasury/bank-statement/view/${item.id}`} className="btn btn-icon btn-sm btn-light-primary">
                                        <span className="svg-icon svg-icon-3">
                                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black" />
                                            <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM6.5 15C5.1 15 4 16.1 4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15Z" fill="black" />
                                          </svg>
                                        </span>
                                      </Link>
                                      { hasLiveCounts && !isFullyReconciled &&
                                        <Link to={`/main/treasury/bank-statement/view/${item.id}`} className="btn btn-sm btn-light-warning">
                                          {lang.labels.finishReconciliation}
                                        </Link>
                                      }
                                      { (() => {
                                        const mc = getMatchedCount(item)
                                        const isBusy = deletingId === item.id || invalidatingId === item.id
                                        const disabled = mc > 0 || isBusy
                                        const tooltip = mc > 0 ? (lang.labels.cannotDeleteMatchedLinesTooltip ?? '').replace('{count}', String(mc)) : ''
                                        return (
                                          <>
                                            { item.import_status !== 'draft' &&
                                              <button
                                                className="btn btn-sm btn-light"
                                                onClick={() => handleInvalidateStatement(item)}
                                                disabled={disabled}
                                                title={tooltip || (lang.labels.invalidateStatement ?? 'Invalidate')}
                                              >
                                                { invalidatingId === item.id
                                                  ? <span className="spinner-border spinner-border-sm"></span>
                                                  : lang.labels.invalidateStatement
                                                }
                                              </button>
                                            }
                                            <button
                                              className="btn btn-icon btn-sm btn-light-danger"
                                              onClick={() => handleDeleteStatement(item)}
                                              disabled={disabled}
                                              title={tooltip || (lang.labels.deleteStatement ?? 'Delete statement')}
                                            >
                                              { deletingId === item.id
                                                ? <span className="spinner-border spinner-border-sm"></span>
                                                : <span className="svg-icon svg-icon-3">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                      <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="currentColor"/>
                                                      <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="currentColor"/>
                                                      <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="currentColor"/>
                                                    </svg>
                                                  </span>
                                              }
                                            </button>
                                          </>
                                        )
                                      })()}
                                    </div>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                          :
                          <tbody>
                            <tr className="text-secondary fw-bold row-bordered">
                              <td colSpan={8} className="text-center">{lang.labels.noRecordsFound}</td>
                            </tr>
                          </tbody>
                        }
                      </>
                      :
                      <tbody>
                        <tr>
                          <td colSpan={8} className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">{lang.labels.loading}</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
              </div>
            </div>
          </div>
          <AppModalBankStatementImport companyForUser={companyForUser} treasuryAccountsForCompany={treasuryAccountsForCompany} defaultTreasuryId={selectedTreasuryId} onImported={(idBankStatement) => { if (idBankStatement) { navigate(`/main/treasury/bank-statement/view/${idBankStatement}`) } else { loadStatements(false) } }}></AppModalBankStatementImport>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

interface ReconciliationProgressCellProps {
  matched?: number
  total?: number
  job: AutoMatchJob
  lang: any
}

const ReconciliationProgressCell: React.FunctionComponent<ReconciliationProgressCellProps> = ({matched, total, job, lang}) => {
  // Job status indicators (diagnostic — queued/running/failed)
  if (job?.status === 'queued') {
    return (
      <div className="d-flex align-items-center">
        <span className="spinner-border spinner-border-sm text-primary me-2"></span>
        <small className="text-muted">{lang.labels.jobQueued}</small>
      </div>
    )
  }

  if (job?.status === 'running') {
    const percent = job.total_lines > 0 ? Math.round((job.processed_lines / job.total_lines) * 100) : 0

    return (
      <div className="d-flex flex-column">
        <div className="d-flex align-items-center mb-1">
          <span className="spinner-border spinner-border-sm text-primary me-2"></span>
          <small>{job.processed_lines} / {job.total_lines}</small>
        </div>
        <div className="progress" style={{height: '4px', width: '120px'}}>
          <div className="progress-bar bg-primary progress-bar-striped progress-bar-animated" style={{width: `${percent}%`}}></div>
        </div>
      </div>
    )
  }

  if (job?.status === 'failed') {
    return (
      <div className="d-flex align-items-center" title={job.error ?? ''}>
        <span className="badge badge-light-danger">{lang.labels.jobFailed}</span>
      </div>
    )
  }

  // Live reconciliation counts
  if (typeof matched !== 'number' || typeof total !== 'number') {
    return <span className="text-muted">—</span>
  }

  const fullyReconciled = matched === total && total > 0

  return (
    <div className="d-flex flex-column">
      <span className={`badge badge-light-${fullyReconciled ? 'success' : 'warning'} mb-1`}>
        {matched}/{total}
      </span>
    </div>
  )
}

export default BankStatementListPage;
